import React from 'react';
import { buildDisplayModel, Entry, Section } from '../utils/display';

interface StructuredViewProps {
  data: unknown;
  docTypeLabel: string;
  includeEmpty: boolean;
}

const DOC_ICONS: Record<string, string> = {
  invoice: '🧾',
  bill: '🧾',
  receipt: '🧾',
  'purchase order': '📦',
  'business card': '👤',
  'id document': '🪪',
  generic: '📄',
};

function iconFor(label: string): string {
  return DOC_ICONS[label.toLowerCase()] || '📄';
}

const ValueCell: React.FC<{ entry: Entry }> = ({ entry }) => {
  if (entry.empty) return <span className="sv__value sv__value--empty">—</span>;
  if (entry.link === 'email') {
    return (
      <a className="sv__value sv__link" href={`mailto:${entry.value}`}>
        {entry.value}
      </a>
    );
  }
  if (entry.link === 'url') {
    const href = /^https?:\/\//i.test(entry.value) ? entry.value : `https://${entry.value}`;
    return (
      <a className="sv__value sv__link" href={href} target="_blank" rel="noreferrer noopener">
        {entry.value}
      </a>
    );
  }
  return (
    <span className={`sv__value${entry.money ? ' sv__value--money' : ''}`}>{entry.value}</span>
  );
};

const SectionBlock: React.FC<{ section: Section }> = ({ section }) => {
  if (section.kind === 'details') {
    return (
      <div className="sv__section">
        <h4 className="sv__section-title">{section.title}</h4>
        <dl className="sv__grid">
          {section.entries.map((e, i) => (
            <div className="sv__pair" key={`${e.label}:${i}`}>
              <dt className="sv__key">{e.label}</dt>
              <dd className="sv__val">
                <ValueCell entry={e} />
              </dd>
            </div>
          ))}
        </dl>
      </div>
    );
  }

  if (section.kind === 'table') {
    return (
      <div className="sv__section">
        <h4 className="sv__section-title">{section.title}</h4>
        <div className="sv__table-wrap">
          <table className="sv__table">
            <thead>
              <tr>
                {section.columns.map((c) => (
                  <th key={c.key} className={c.money ? 'sv__th--money' : undefined}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.rows.map((row, ri) => (
                <tr key={ri}>
                  {section.columns.map((c) => (
                    <td key={c.key} className={c.money ? 'sv__td--money' : undefined}>
                      {row[c.key] || <span className="sv__value--empty">—</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // list
  return (
    <div className="sv__section">
      <h4 className="sv__section-title">{section.title}</h4>
      <div className="sv__chips">
        {section.items.map((item, i) => (
          <span className="sv__chip" key={`${item}:${i}`}>
            {item}
          </span>
        ))}
      </div>
    </div>
  );
};

const StructuredView: React.FC<StructuredViewProps> = ({ data, docTypeLabel, includeEmpty }) => {
  const model = buildDisplayModel(data, docTypeLabel, { includeEmpty });
  const hasContent = model.sections.length > 0 || model.totals.length > 0;

  if (!hasContent) {
    return <p className="muted sv__empty">No fields were extracted from this document.</p>;
  }

  return (
    <div className="sv">
      <div className="sv__head">
        <span className="sv__icon" aria-hidden="true">
          {iconFor(docTypeLabel)}
        </span>
        <div className="sv__headtext">
          <h3 className="sv__title">{model.title}</h3>
          {model.subtitle && <p className="sv__subtitle">{model.subtitle}</p>}
        </div>
      </div>

      <div className="sv__body">
        {model.sections.map((section, i) => (
          <SectionBlock section={section} key={`${section.title}:${i}`} />
        ))}
      </div>

      {model.totals.length > 0 && (
        <div className="sv__totals">
          <h4 className="sv__section-title">
            Totals{model.currency ? <span className="sv__currency"> · {model.currency}</span> : null}
          </h4>
          <div className="sv__totals-rows">
            {model.totals.map((t, i) => (
              <div className={`sv__total-row${t.primary ? ' sv__total-row--primary' : ''}`} key={i}>
                <span className="sv__total-label">{t.label}</span>
                <span className="sv__total-value">{t.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StructuredView;
